import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from './cx';

export type ToastTone = 'progress' | 'attention' | 'alert';

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

const TONE_CLASS: Record<ToastTone, string> = {
  progress: 'bg-teal-600 text-white',
  attention: 'bg-attention-soft text-attention-ink',
  alert: 'bg-alert-soft text-alert-ink',
};

const AUTO_DISMISS_MS = 4000;
const MAX_STACKED = 2;

type ToastFn = (message: string, opts?: { tone?: ToastTone }) => void;

const ToastContext = createContext<ToastFn | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback<ToastFn>(
    (message, opts) => {
      const id = ++nextId.current;
      const tone = opts?.tone ?? 'progress';
      setToasts((prev) => {
        const next = [...prev, { id, message, tone }];
        return next.length > MAX_STACKED ? next.slice(next.length - MAX_STACKED) : next;
      });
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), AUTO_DISMISS_MS),
      );
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((timer) => clearTimeout(timer));
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role="status"
            aria-live="polite"
            className={cx(
              'pointer-events-auto rounded-md px-4 py-3 text-label shadow-2 transition-opacity duration-fade',
              TONE_CLASS[toast.tone],
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastFn => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return useMemo(() => ctx, [ctx]);
};
